import React, { useEffect } from 'react';
import { useLatest } from 'react-use';

import { pluralizeWithCount } from '@/features/common';

import {
  EAutomationType,
  TAutomationTriggerMap,
  TTriggerNotificationSettings,
} from '../../types';

export const TRIGGER_RUN_DELAY = 10000;

export const TRIGGER_NOTIFICATION_DEFAULTS: {
  [t in TTriggerNotificationSettings['type']]: Omit<
    TTriggerNotificationSettings,
    'type'
  >;
} = {
  [EAutomationType.SendEmailToMovedCandidate]: {
    title: 'Automation triggered',
    description: (candidateCount) => (
      <>
        An email will be sent to{' '}
        <b>{pluralizeWithCount(candidateCount, 'candidate')}.</b>
      </>
    ),
    successMessage: 'Emails have been sent',
  },
  [EAutomationType.SendTestInviteToMovedCandidate]: {
    title: 'Automation triggered',
    description: (candidateCount) => (
      <>
        An invitation will be sent to{' '}
        <b>{pluralizeWithCount(candidateCount, 'candidate')}.</b>
      </>
    ),
    successMessage: 'Emails have been sent',
  },
};

/** Run all automation triggers on browser/tab close or refresh */
export const useBeforeUnloadCheck = (
  automationTriggers: TAutomationTriggerMap,
  runAutomationTrigger: (id: string) => void
) => {
  const automationTriggersRef = useLatest(automationTriggers);
  const runAutomationTriggerRef = useLatest(runAutomationTrigger);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      const triggers = automationTriggersRef.current;

      if (triggers.size > 0) {
        triggers.forEach((_, id) => runAutomationTriggerRef.current(id));

        // Prevent the browser from closing
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);

    // useLatest provides stable refs, so dependencies aren't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};
